import { createFileRoute } from "@tanstack/react-router";
import { HardHat, Layers, BarChart3, Users, FileText, ShieldCheck } from "lucide-react";
import { SolutionLanding } from "@/components/landing/SolutionLanding";

const URL = "https://busditeads.com/software-de-gestion-de-construccion";
const TITLE = "Software de Gestión de Construcción para Contratistas — Business Dite Ads";
const DESC =
  "Software de gestión de construcción: obras, cuadrillas, control horario, presupuestos, facturación y nómina en una sola plataforma. Prueba gratis 14 días.";

const faqs = [
  {
    q: "¿Qué hace un software de gestión de construcción?",
    a: "Centraliza obras, cuadrillas, plazos, costos y documentos para que el contratista opere cada proyecto con rentabilidad — sin depender de hojas de cálculo, WhatsApp y papel.",
  },
  {
    q: "¿Sirve para contratistas pequeños y medianos?",
    a: "Sí. Business Dite Ads está pensado para empresas de 3 a 200 empleados — albañilería, concreto, techado, eléctrica/hidráulica y contratistas generales.",
  },
  {
    q: "¿Mi cuadrilla puede usarlo desde el móvil?",
    a: "Sí. La cuadrilla ficha con GPS desde cualquier smartphone, sin instalar app. La oficina usa el panel completo en el navegador.",
  },
  { q: "¿Hay permanencia?", a: "No. Planes mensuales, 14 días gratis y cancelas cuando quieras." },
];

export const Route = createFileRoute("/software-de-gestion-de-construccion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "es_ES" },
    ],
    links: [
      { rel: "canonical", href: URL },
      {
        rel: "alternate",
        hrefLang: "en",
        href: "https://busditeads.com/construction-management-software",
      },
      {
        rel: "alternate",
        hrefLang: "pt",
        href: "https://busditeads.com/software-de-gestao-para-construcao",
      },
      { rel: "alternate", hrefLang: "es", href: URL },
      {
        rel: "alternate",
        hrefLang: "x-default",
        href: "https://busditeads.com/construction-management-software",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          inLanguage: "es",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SolutionLanding
      eyebrow="Software de Gestión de Construcción"
      h1="Gestiona cada obra, cuadrilla y dólar desde una sola plataforma"
      subhead="Business Dite Ads une obras, control horario, presupuestos, facturación y nómina — creado para empresas de construcción que quieren rentabilidad por obra, no solo papeleo."
      primaryCtaLabel="Empezar prueba gratis de 14 días"
      secondaryCtaLabel="Ver precios"
      benefits={[
        "Sin tarjeta de crédito",
        "Configuración en menos de 1 hora",
        "Onboarding gratuito",
        "Cancela cuando quieras",
      ]}
      featuresHeading="Todo lo que un contratista necesita en un panel"
      features={[
        {
          icon: HardHat,
          title: "Obras y proyectos",
          description:
            "Planifica obras, asigna cuadrillas, sigue el avance y mantén cada documento ligado al proyecto correcto.",
        },
        {
          icon: Layers,
          title: "Del presupuesto a la factura",
          description:
            "Convierte presupuestos asistidos por IA en contratos firmados y facturas inmutables — sin volver a tipear nada.",
        },
        {
          icon: BarChart3,
          title: "Rentabilidad por obra",
          description:
            "Mira costo real vs. ingreso por obra: mano de obra, materiales, gastos y margen actualizados en vivo.",
        },
        {
          icon: Users,
          title: "Cuadrillas y subcontratistas",
          description:
            "Gestiona empleados y subcontratistas, tarifas por hora o por día y roles con permisos granulares.",
        },
        {
          icon: FileText,
          title: "Documentos y aprobaciones",
          description:
            "Aprobación de factura por el cliente, contratos firmados y recibos adjuntos a cada gasto.",
        },
        {
          icon: ShieldCheck,
          title: "Listo para auditoría",
          description:
            "Historial inmutable, acceso por rol y almacenamiento seguro en la nube para el cierre fiscal.",
        },
      ]}
      howItWorksHeading="Del alta a la primera obra rentable en pocos días"
      steps={[
        {
          title: "Importa obras y cuadrilla",
          description:
            "Carga proyectos activos, empleados y tarifas. Te ayudamos a migrar desde tus hojas de cálculo.",
        },
        {
          title: "La cuadrilla ficha con GPS",
          description:
            "Los trabajadores fichan desde su móvil. La oficina ve horas, ubicaciones y costos en tiempo real.",
        },
        {
          title: "Factura, paga y ve el margen",
          description:
            "Envía facturas, corre la nómina y observa el margen por obra actualizándose automáticamente.",
        },
      ]}
      socialProofHeading="Por qué los contratistas eligen Business Dite Ads"
      socialProof={[
        "Reemplaza 4–5 herramientas desconectadas por una sola plataforma",
        "Deja de perder horas en hojas de cálculo y partes manuales",
        "Detecta obras no rentables antes de que cuesten dinero",
        "Paga a la cuadrilla con exactitud — por hora o por día",
        "Cobra más rápido con facturas aprobadas por el cliente",
        "Disponible en español, portugués e inglés",
      ]}
      faqHeading="Preguntas frecuentes"
      faqs={faqs}
      finalCtaHeading="¿Listo para llevar tu constructora como un profesional?"
      finalCtaSubhead="Únete a contratistas que cambiaron papel, hojas de cálculo y 5 apps por Business Dite Ads."
    />
  );
}
